import type {
  ActiveCall,
  ConsentRecord,
  DncImport,
  EndpointStatus,
  PhoneIntel,
  ScrubJob,
  SettingsMap,
  HealthDetail,
  AgentPerf,
  AuditEntry,
  Campaign,
  CampaignPerf,
  CampaignSummary,
  CallLogPage,
  ComplianceReport,
  Disposition,
  DncPage,
  HourlyPoint,
  ImportPreview,
  ImportReport,
  LeadDetail,
  LeadPage,
  LeadSearchResult,
  ListSummary,
  LiveBoard,
  Overview,
  QueueDepth,
  Session,
  Trunk,
  Did,
  DidPool,
  UserRow,
  PacingDecisionRow,
  PacingExplain,
  PacingInputs,
  MonitorMode,
  CallTimeline,
  LeadExplanation,
  SimResult,
} from './types'

/**
 * The token lives in module scope, not localStorage.
 *
 * An appliance in a call centre sits on shared workstations where the next
 * person to sit down is a different agent. A token in localStorage survives
 * that; a token in memory does not.
 */
let token: string | null = null

export function setToken(t: string | null) {
  token = t
}
export function getToken(): string | null {
  return token
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly reasonCode?: string,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {}
  if (!(init?.body instanceof FormData)) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(path, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } })

  if (!res.ok) {
    let message = res.statusText
    let code = 'error'
    let reasonCode: string | undefined
    try {
      const body = await res.json()
      message = body.message ?? message
      code = body.error ?? code
      reasonCode = body.reason_code
    } catch {
      // A non-JSON error body means something upstream answered, usually a
      // proxy. The status alone is the honest thing to report.
    }
    throw new ApiError(message, res.status, code, reasonCode)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

const get = <T,>(p: string) => request<T>(p)
const post = <T,>(p: string, body?: unknown) =>
  request<T>(p, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
const put = <T,>(p: string, body: unknown) =>
  request<T>(p, { method: 'PUT', body: JSON.stringify(body) })
const patch = <T,>(p: string, body: unknown) =>
  request<T>(p, { method: 'PATCH', body: JSON.stringify(body) })
const del = <T,>(p: string) => request<T>(p, { method: 'DELETE' })

const qs = (params: Record<string, string | number | undefined | null>) => {
  const s = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') s.set(k, String(v))
  }
  const out = s.toString()
  return out ? `?${out}` : ''
}

export const api = {
  // ------------------------------------------------------------- auth ----
  login: (username: string, password: string) =>
    post<Session>('/api/auth/login', { username, password }),
  me: () => get<{ session_id: string | null; state: string | null }>('/api/me'),

  // -------------------------------------------------------- dashboard ----
  overview: (p: { from?: string; to?: string; campaign_id?: string } = {}) =>
    get<Overview>(`/api/stats/overview${qs(p)}`),
  hourly: (p: { from?: string; to?: string; campaign_id?: string } = {}) =>
    get<HourlyPoint[]>(`/api/stats/hourly${qs(p)}`),
  dispositionBreakdown: (p: { from?: string; to?: string; campaign_id?: string } = {}) =>
    get<{ code: string; label: string; count: number }[]>(`/api/stats/dispositions${qs(p)}`),

  // -------------------------------------------------------- campaigns ----
  campaigns: () => get<CampaignSummary[]>('/api/campaigns'),
  campaign: (id: string) => get<Campaign>(`/api/campaigns/${id}`),
  createCampaign: (c: unknown) => post<{ id: string }>('/api/campaigns', c),
  updateCampaign: (id: string, c: unknown) => put<{ ok: true }>(`/api/campaigns/${id}`, c),
  setCampaignActive: (id: string, active: boolean) =>
    post<{ ok: true; active: boolean }>(`/api/campaigns/${id}/active`, { active }),
  deleteCampaign: (id: string) => del<{ ok: true }>(`/api/campaigns/${id}`),

  // ------------------------------------------------------------ lists ----
  lists: (campaignId: string) => get<ListSummary[]>(`/api/campaigns/${campaignId}/lists`),
  createList: (campaignId: string, name: string, priority = 0) =>
    post<{ id: string }>(`/api/campaigns/${campaignId}/lists`, { name, priority, active: true }),
  deleteList: (id: string) => del<{ ok: true; leads_deleted: number }>(`/api/lists/${id}`),

  // ------------------------------------------------------------ leads ----
  leads: (p: {
    q?: string
    status?: string
    list_id?: string
    campaign_id?: string
    page?: number
    per_page?: number
  }) => get<LeadPage>(`/api/leads${qs(p)}`),
  lead: (id: string) => get<LeadDetail>(`/api/leads/${id}`),
  updateLead: (id: string, patchBody: unknown) => patch<{ ok: true }>(`/api/leads/${id}`, patchBody),
  deleteLead: (id: string) => del<{ ok: true }>(`/api/leads/${id}`),

  // ----------------------------------------------------------- import ----
  importPreview: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request<ImportPreview>('/api/import/preview', { method: 'POST', body: fd })
  },
  importLeads: (listId: string, file: File, mapping: unknown) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('mapping', JSON.stringify(mapping))
    return request<ImportReport>(`/api/lists/${listId}/import`, { method: 'POST', body: fd })
  },

  // ------------------------------------------------------------ users ----
  users: () => get<UserRow[]>('/api/users'),
  createUser: (u: unknown) =>
    post<{ id: string; sip_username?: string; sip_password?: string; pjsip_config?: string }>(
      '/api/users',
      u,
    ),
  updateUser: (id: string, u: unknown) => patch<{ ok: true }>(`/api/users/${id}`, u),

  // --------------------------------------------------------- telephony ----
  trunks: () => get<Trunk[]>('/api/trunks'),
  createTrunk: (t: unknown) =>
    post<{ id: string; pjsip_config: string; note: string }>('/api/trunks', t),
  deleteTrunk: (id: string) => del<{ ok: true }>(`/api/trunks/${id}`),
  dids: () => get<Did[]>('/api/dids'),
  createDid: (d: unknown) => post<{ id: string }>('/api/dids', d),
  flagDid: (id: string) => post<{ ok: true }>(`/api/dids/${id}/flag`),
  didPools: () => get<DidPool[]>('/api/did-pools'),
  createDidPool: (name: string, strategy: string) =>
    post<{ id: string }>('/api/did-pools', { name, strategy }),

  // ----------------------------------------------------- dispositions ----
  dispositions: () => get<Disposition[]>('/api/dispositions'),
  campaignDispositions: (campaignId: string) =>
    get<Disposition[]>(`/api/campaigns/${campaignId}/dispositions`),
  createDisposition: (d: unknown) => post<{ id: string }>('/api/dispositions', d),
  deleteDisposition: (id: string) => del<{ ok: true }>(`/api/dispositions/${id}`),

  // -------------------------------------------------------------- DNC ----
  dnc: (p: { q?: string; page?: number } = {}) => get<DncPage>(`/api/dnc${qs(p)}`),
  addDnc: (numbers: string, scope = 'internal', reason?: string) =>
    post<{ added: number; rejected: string[] }>('/api/dnc', {
      numbers,
      scope,
      reason,
      source: 'manual',
    }),
  removeDnc: (id: number) => del<{ ok: true }>(`/api/dnc/${id}`),

  // ---------------------------------------------------------- reports ----
  campaignPerformance: (p: { from?: string; to?: string } = {}) =>
    get<CampaignPerf[]>(`/api/stats/campaigns${qs(p)}`),
  agentPerformance: (p: { from?: string; to?: string } = {}) =>
    get<AgentPerf[]>(`/api/stats/agents${qs(p)}`),
  callLog: (p: {
    from?: string
    to?: string
    campaign_id?: string
    agent_id?: string
    q?: string
    page?: number
  }) => get<CallLogPage>(`/api/stats/calls${qs(p)}`),
  recording: (callId: string) =>
    get<{ sha256: string | null; bytes: number | null; path: string; verified: boolean }>(
      `/api/stats/recording/${callId}`,
    ),
  compliance: (p: { from?: string; to?: string } = {}) =>
    get<ComplianceReport>(`/api/stats/compliance${qs(p)}`),
  audit: (p: { page?: number } = {}) => get<AuditEntry[]>(`/api/audit${qs(p)}`),

  // ------------------------------------------------------ supervisor ----
  // Live controls. Every one of these is audited server-side: who listened to
  // whose call, and when.
  monitorStart: (supervisorSession: string, targetSession: string, mode: MonitorMode) =>
    post<{ ok: boolean; mode: string }>('/api/supervisor/monitor', {
      supervisor_session: supervisorSession,
      target_session: targetSession,
      mode,
    }),
  monitorStop: (supervisorSession: string) =>
    post<{ ok: boolean }>('/api/supervisor/monitor/stop', {
      supervisor_session: supervisorSession,
    }),
  forceHangup: (sessionId: string) =>
    post<{ ok: boolean }>('/api/supervisor/hangup', { session_id: sessionId }),
  forceLogout: (sessionId: string) =>
    post<{ ok: boolean }>('/api/supervisor/logout', { session_id: sessionId }),

  // ----------------------------------------------------------- pacing ----
  // Why the dialler did what it did. Every launch and every change of
  // governor, with the complete inputs the decision was made from.
  pacingDecisions: (
    p: {
      campaign_id?: string
      governor?: string
      dialed_only?: string
      limit?: number
    } = {},
  ) => get<{ decisions: PacingDecisionRow[] }>(`/api/pacing/decisions${qs(p)}`),

  // Replay a stored decision through the controller running right now. If the
  // answer differs from what was recorded, the pacing logic changed in
  // between -- which is the thing worth being able to establish rather than
  // assume.
  pacingExplain: (inputs: PacingInputs) =>
    post<PacingExplain>('/api/pacing/explain', inputs),

  // -------------------------------------------------------- inspection ----
  callTimeline: (callId: string) =>
    get<CallTimeline>(`/api/calls/${callId}/timeline`),
  explainLead: (leadId: string) =>
    get<LeadExplanation>(`/api/leads/${leadId}/explain`),
  // Runs the real controller against a hypothetical campaign, server-side.
  simulatePacing: (body: {
    agents: number
    answer_rate: number
    machine_rate: number
    avg_talk_sec?: number
    avg_wrap_sec?: number
    abandon_target?: number
    max_ratio?: number
  }) => post<SimResult>('/api/pacing/simulate', body),

  // ------------------------------------------------------------- live ----
  liveAgents: () => get<LiveBoard>('/api/live/agents'),
  activeCalls: () => get<ActiveCall[]>('/api/live/calls'),
  queues: () => get<QueueDepth[]>('/api/live/queues'),

  // ------------------------------------------------------ agent station ----
  agentLogin: (campaignId: string | null) =>
    post<{ session_id: string }>('/api/agent/login', { campaign_id: campaignId }),
  agentLogout: (sessionId: string) => post<void>('/api/agent/logout', { session_id: sessionId }),
  setState: (sessionId: string, state: 'ready' | 'paused', pauseCode?: string) =>
    post<void>('/api/agent/state', {
      session_id: sessionId,
      state,
      pause_code: pauseCode ?? null,
    }),
  dial: (sessionId: string, phone: string, leadId?: string) =>
    post<{ call_id: string; dialed: string }>('/api/agent/dial', {
      session_id: sessionId,
      phone,
      lead_id: leadId ?? null,
    }),
  hangup: (sessionId: string) => post<void>('/api/agent/hangup', { session_id: sessionId }),
  dispose: (sessionId: string, callId: string, dispositionId: string, note?: string) =>
    post<void>('/api/agent/dispose', {
      session_id: sessionId,
      call_id: callId,
      disposition_id: dispositionId,
      note: note ?? null,
    }),
  searchLeads: (campaignId: string, q: string) =>
    get<LeadSearchResult[]>(`/api/campaigns/${campaignId}/leads/search?q=${encodeURIComponent(q)}`),

  healthDetail: () => get<HealthDetail>('/api/health/detail'),

  dialNext: (sessionId: string) =>
    post<{ dialed: boolean; call_id?: string; reason?: string }>('/api/agent/dial-next', {
      session_id: sessionId,
    }),

  endpointStatus: () => get<EndpointStatus>('/api/agent/endpoint'),

  // -------------------------------------------------- scrub & compliance ----
  settings: () => get<SettingsMap>('/api/settings'),
  updateSettings: (v: Record<string, unknown>) => patch<{ updated: string[] }>('/api/settings', v),

  startScrub: (p: { list_id?: string; campaign_id?: string }) =>
    post<{ job_id: string }>('/api/scrub', p),
  scrubJobs: () => get<ScrubJob[]>('/api/scrub/jobs'),
  cancelScrub: (id: string) => del<{ ok: true }>(`/api/scrub/jobs/${id}`),
  phoneIntel: (phone: string) =>
    get<PhoneIntel>(`/api/scrub/phone/${encodeURIComponent(phone)}`),

  consent: (p: { q?: string; page?: number } = {}) =>
    get<{ total: number; records: ConsentRecord[] }>(`/api/consent${qs(p)}`),
  addConsent: (c: unknown) => post<{ id: string }>('/api/consent', c),
  revokeConsent: (id: string, reason: string) =>
    post<{ ok: true }>(`/api/consent/${id}/revoke`, { reason }),

  dncImports: () => get<DncImport[]>('/api/dnc/imports'),
  rollbackDncImport: (id: string) => del<{ removed: number }>(`/api/dnc/imports/${id}`),
  importDnc: (file: File, scope: string, scopeRef?: string, source?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('scope', scope)
    if (scopeRef) fd.append('scope_ref', scopeRef)
    fd.append('source', source ?? file.name)
    return request<{
      total_rows: number
      added: number
      duplicates: number
      rejected: number
    }>('/api/dnc/import', { method: 'POST', body: fd })
  },

  health: () => get<{ status: string; database: boolean; media: boolean }>('/api/health'),
}

/**
 * Connect the agent desk websocket.
 *
 * The socket carries state one way only. Every action goes over the REST API,
 * so losing it costs the live view and never a call -- and the reconnect
 * replaces the whole view with a fresh snapshot rather than trying to catch up
 * on events it missed.
 */
export function connectDesk(
  sessionId: string,
  onEvent: (e: unknown) => void,
  onStatus: (connected: boolean) => void,
): () => void {
  let socket: WebSocket | null = null
  let closed = false
  let retry = 1000

  const open = () => {
    if (closed) return
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const url = `${proto}://${location.host}/ws/desk?token=${encodeURIComponent(
      token ?? '',
    )}&session_id=${encodeURIComponent(sessionId)}`

    socket = new WebSocket(url)
    socket.onopen = () => {
      retry = 1000
      onStatus(true)
    }
    socket.onmessage = (ev) => {
      try {
        onEvent(JSON.parse(ev.data))
      } catch {
        // Malformed frame; the next snapshot corrects whatever it would have said.
      }
    }
    socket.onclose = () => {
      onStatus(false)
      if (closed) return
      setTimeout(open, retry)
      retry = Math.min(retry * 2, 15000)
    }
    socket.onerror = () => socket?.close()
  }

  open()
  return () => {
    closed = true
    socket?.close()
  }
}
